import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { checkAuth as apiCheckAuth, logout as apiLogout, setOn401Handler } from '../auth/Auth';
import { csrfHeadersFor } from '../api';

const AuthContext = createContext();

const API_BASE = process.env.REACT_APP_API_URL || '';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Handle 401 responses globally - called when session expires
  const handleSessionExpired = useCallback(() => {
    console.log('[AuthContext] Session expired, logging out');
    setIsAuthenticated(false);
    setUser(null);
    setSessionExpired(true);
    // Clear any legacy localStorage token
    try { localStorage.removeItem('token'); } catch {}
    // Redirect to login with expired flag
    window.location.href = '/login?expired=1';
  }, []);

  // Set up global 401 handler on mount
  useEffect(() => {
    setOn401Handler(handleSessionExpired);
  }, [handleSessionExpired]);

  // Check auth status on mount (verifies httpOnly cookie with backend)
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const result = await apiCheckAuth();
        setIsAuthenticated(result.authenticated);
        setUser(result.user);
      } catch (e) {
        console.error('Auth verification failed:', e);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    verifyAuth();
  }, []);

  // Called after successful login - just updates state
  // The httpOnly cookie is already set by the backend
  const login = useCallback((userData = null) => {
    setIsAuthenticated(true);
    setUser(userData);
  }, []);

  // Logout - calls backend to clear the httpOnly cookie
  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (e) {
      console.error('Logout error:', e);
    }
    setIsAuthenticated(false);
    setUser(null);
    // Also clear any legacy localStorage token
    try { localStorage.removeItem('token'); } catch {}
  }, []);

  // Refresh auth state (useful after actions that might change user state)
  const refreshAuth = useCallback(async () => {
    try {
      const result = await apiCheckAuth();
      setIsAuthenticated(result.authenticated);
      setUser(result.user);
      return result;
    } catch (e) {
      setIsAuthenticated(false);
      setUser(null);
      return { authenticated: false, user: null };
    }
  }, []);

  // ---- Workspace switching ("act as agent" overlay) -----------------------

  // Switch into another agent's workspace. Pass null/undefined to switch
  // back to the operator's own workspace (Agency View).
  const switchToAgent = useCallback(async (agentUserId) => {
    try {
      // null/undefined means exit the overlay (go back to Agency View).
      // A real agentUserId always goes through /switch, including self-switch
      // (owner clicking My Workspace to view their own agent book).
      if (!agentUserId) {
        await fetch(`${API_BASE}/api/workspace/exit`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        });
      } else {
        const res = await fetch(`${API_BASE}/api/workspace/switch`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
          body: JSON.stringify({ agentUserId }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to switch workspace');
        }
      }
      // Hard reload so every component re-fetches with the new overlay context.
      // When exiting back to owner workspace, send agency users to their dashboard.
      const isAgencyRole = user && ['agency', 'agency_owner', 'agency_admin'].includes(user.role);
      const dest = (!agentUserId && isAgencyRole) ? '/agency/dashboard' : '/dashboard';
      window.location.assign(dest);
    } catch (e) {
      console.error('[AuthContext] switchToAgent error:', e);
      alert(e.message || 'Failed to switch workspace');
    }
  }, [user]);

  const exitWorkspace = useCallback(async () => {
    await switchToAgent(null);
  }, [switchToAgent]);

  // For backwards compatibility - token is now in httpOnly cookie
  // This returns null but keeps the interface consistent
  const token = null;

  return (
    <AuthContext.Provider value={{
      token,
      user,
      isAuthenticated,
      loading,
      login,
      logout,
      refreshAuth,
      switchToAgent,
      exitWorkspace,
      // Convenience: when user.actingAs is set, the operator is overlaid.
      isActingAs: !!(user && user.actingAs),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
